// VOY-485 (rebuild) — Privacy (Settings tab).
//
// Two sub-blocks in one card:
//
// 1. Privacy toggles (local to this section)
//    public_traveler_profile, personalized_recommendations,
//    anonymous_analytics, marketing_emails, third_party_offers.
//    Read/write via getPreferences / savePreferences with
//    preference_type='privacy'.
//
// 2. PrivacyControls component from VOY-465 — MOUNTED, not rebuilt.
//    Handles Export / Wipe / Account delete (with re-auth + 7-day grace).
//    No parallel destructive flow.

import { useEffect, useState, type ChangeEvent } from 'react'
import { getPreferences, savePreferences } from '@/lib/profile'
import { PrivacyControls } from '@/components/profile/privacy/PrivacyControls'

export interface PrivacySectionProps {
  userId: string
  /** Forwarded to PrivacyControls — caller decides whether to sign out / banner. */
  onAfterDelete?: () => void
}

interface PrivacyPrefs {
  public_traveler_profile?: boolean
  personalized_recommendations?: boolean
  anonymous_analytics?: boolean
  marketing_emails?: boolean
  third_party_offers?: boolean
}

const TOGGLES: { key: keyof PrivacyPrefs; label: string; hint?: string; defaultValue?: boolean }[] = [
  { key: 'public_traveler_profile', label: 'Public traveler profile', hint: 'Friends + Voy users can see badges', defaultValue: true },
  { key: 'personalized_recommendations', label: 'Personalized recommendations', hint: 'Voy uses your history to improve picks', defaultValue: true },
  { key: 'anonymous_analytics', label: 'Anonymous analytics', defaultValue: true },
  { key: 'marketing_emails', label: 'Marketing emails' },
  { key: 'third_party_offers', label: 'Third-party offers (airlines, hotels)' },
]

export function PrivacySection({ userId, onAfterDelete }: PrivacySectionProps) {
  const [prefs, setPrefs] = useState<PrivacyPrefs>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<keyof PrivacyPrefs | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await getPreferences(userId, 'privacy')
        if (!cancelled) setPrefs((data as PrivacyPrefs) ?? {})
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId])

  async function toggle(key: keyof PrivacyPrefs, e: ChangeEvent<HTMLInputElement>) {
    const next = { ...prefs, [key]: e.target.checked }
    setPrefs(next)
    setSaving(key)
    try {
      await savePreferences(userId, 'privacy', next as Record<string, unknown>)
    } catch (err) {
      console.error('[PrivacySection] save failed:', err)
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="card" aria-labelledby="privacy-h">
      <div className="card-h">
        <h3 id="privacy-h">Privacy</h3>
        {saving && <span className="meta">SAVING…</span>}
      </div>
      <div className="stack sm">
        {TOGGLES.map(({ key, label, hint, defaultValue }) => {
          const checked = prefs[key] ?? Boolean(defaultValue)
          return (
            <div className="toggle-row" key={key}>
              <div>
                <div className="toggle-row-label">{label}</div>
                {hint && <div className="toggle-row-hint">{hint}</div>}
              </div>
              <label className="switch">
                <input type="checkbox" checked={checked} disabled={loading} aria-label={label} onChange={(e) => void toggle(key, e)} />
                <span className="track" />
              </label>
            </div>
          )
        })}
      </div>

      <div className="divider" />
      <PrivacyControls userId={userId} onAfterDelete={onAfterDelete} />
    </section>
  )
}
